import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CatalogMaster({ user, onLogout }) {
  const navigate = useNavigate();
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [brands, setBrands] = useState([]);
  const [filterBrand, setFilterBrand] = useState('');
  const [syncing, setSyncing] = useState(false);
  
  // Masters data (flat structure)
  const [masterBrands, setMasterBrands] = useState([]);
  const [masterModels, setMasterModels] = useState([]);
  const [masterVariants, setMasterVariants] = useState([]);
  const [masterColors, setMasterColors] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    variant: '',
    color: '',
    title: '',
    description: '',
    current_price: 0,
    old_price: null,
    custom_discount: 0,
    specifications: {},
    is_active: true
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedForShare, setSelectedForShare] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTitle, setShareTitle] = useState('Vehicle Catalog');
  const [shareMessage, setShareMessage] = useState('');
  const [sharePhone, setSharePhone] = useState('');
  const [lastShareUrl, setLastShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);

  const getAuthHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  const fetchCatalog = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterBrand ? { brand: filterBrand } : {};
      const response = await axios.get(`${BACKEND_URL}/api/catalog`, {
        ...getAuthHeader(),
        params
      });
      setCatalogItems(response.data || []);
      
      // Get distinct brands
      const brandsRes = await axios.get(`${BACKEND_URL}/api/catalog/brands`, getAuthHeader());
      setBrands(brandsRes.data || []);
    } catch (error) {
      toast.error('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, filterBrand]);

  const fetchVehicleHierarchy = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/masters/vehicle-hierarchy`, getAuthHeader());
      const data = response.data;
      
      // Deduplicate by name
      const dedupeByName = (arr) => {
        const seen = new Set();
        return (arr || []).filter(item => {
          if (seen.has(item.name)) return false;
          seen.add(item.name);
          return true;
        });
      };
      
      setMasterBrands(dedupeByName(data.brands));
      setMasterModels(dedupeByName(data.models));
      setMasterVariants(dedupeByName(data.variants));
      setMasterColors(dedupeByName(data.colors));
    } catch (error) {
      console.error('Failed to fetch vehicle hierarchy');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchCatalog();
    fetchVehicleHierarchy();
  }, [fetchCatalog, fetchVehicleHierarchy]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate title
      if (['brand', 'model', 'variant'].includes(field)) {
        const b = field === 'brand' ? value : prev.brand;
        const m = field === 'model' ? value : prev.model;
        const v = field === 'variant' ? value : prev.variant;
        updated.title = `${b} ${m} ${v}`.trim();
      }
      
      // Reset dependent fields
      if (field === 'brand') {
        updated.model = '';
        updated.variant = '';
        updated.color = '';
      } else if (field === 'model') {
        updated.variant = '';
        updated.color = '';
      } else if (field === 'variant') {
        updated.color = '';
      }
      
      return updated;
    });
  };

  const getModels = () => {
    // Return all models - in this flat structure, models aren't filtered by brand
    return masterModels;
  };

  const getVariants = () => {
    // Return all variants - in this flat structure, variants aren't filtered by model
    return masterVariants;
  };

  const getColors = () => {
    // Return all colors
    return masterColors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.brand || !formData.model || !formData.variant || !formData.title) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      if (editItem) {
        await axios.put(`${BACKEND_URL}/api/catalog/${editItem.id}`, formData, getAuthHeader());
        toast.success('Catalog item updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/catalog`, formData, getAuthHeader());
        toast.success('Catalog item created');
      }
      setShowModal(false);
      setEditItem(null);
      resetForm();
      fetchCatalog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save catalog item');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      brand: item.brand || '',
      model: item.model || '',
      variant: item.variant || '',
      color: item.color || '',
      title: item.title || '',
      description: item.description || '',
      current_price: item.current_price || 0,
      old_price: item.old_price || null,
      custom_discount: item.custom_discount || 0,
      specifications: item.specifications || {},
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this catalog item?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/catalog/${id}`, getAuthHeader());
      toast.success('Catalog item deleted');
      fetchCatalog();
    } catch (error) {
      toast.error('Failed to delete catalog item');
    }
  };

  const handleImageUpload = async (e, itemId) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingImage(true);
      await axios.post(
        `${BACKEND_URL}/api/catalog/${itemId}/upload-image?is_primary=${catalogItems.find(i => i.id === itemId)?.images?.length === 0}`,
        formData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Image uploaded successfully');
      fetchCatalog();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (itemId, imageId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/catalog/${itemId}/image/${imageId}`, getAuthHeader());
      toast.success('Image removed');
      fetchCatalog();
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const handleSetPrimaryImage = async (itemId, imageId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/catalog/${itemId}/image/${imageId}/primary`, {}, getAuthHeader());
      toast.success('Primary image updated');
      fetchCatalog();
    } catch (error) {
      toast.error('Failed to update primary image');
    }
  };

  const handleSyncPrices = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${BACKEND_URL}/api/catalog/sync-prices`, {}, getAuthHeader());
      toast.success(response.data.message);
      fetchCatalog();
    } catch (error) {
      toast.error('Failed to sync prices');
    } finally {
      setSyncing(false);
    }
  };

  const toggleShareItem = (itemId) => {
    setSelectedForShare(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const selectAllForShare = () => {
    if (selectedForShare.length === catalogItems.length) {
      setSelectedForShare([]);
    } else {
      setSelectedForShare(catalogItems.map(i => i.id));
    }
  };

  const handleShareWhatsApp = async () => {
    if (selectedForShare.length === 0) {
      toast.error('Please select at least one catalog item to share');
      return;
    }
    setSharing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/catalog/share/create-page`, {
        item_ids: selectedForShare,
        title: shareTitle,
        message: shareMessage,
      }, getAuthHeader());

      const shareUrl = response.data.share_url;
      setLastShareUrl(shareUrl);
      const msgParts = [shareTitle];
      if (shareMessage) msgParts.push(shareMessage);
      msgParts.push(`${response.data.item_count} vehicle(s)`);
      msgParts.push('');
      msgParts.push(shareUrl);
      const text = encodeURIComponent(msgParts.join('\n'));
      const phone = sharePhone.replace(/[^0-9]/g, '');
      const waUrl = phone
        ? `https://web.whatsapp.com/send?phone=${phone}&text=${text}`
        : `https://web.whatsapp.com/send?text=${text}`;

      // Use anchor element to reliably open in new tab (works inside iframes)
      const a = document.createElement('a');
      a.href = waUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast.success('Share link created! Opening WhatsApp...');
      setShowShareModal(false);
      setSelectedForShare([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
    }
    setSharing(false);
  };

  const handleShareEmail = async () => {
    if (selectedForShare.length === 0) {
      toast.error('Please select at least one catalog item to share');
      return;
    }
    setSharing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/catalog/share/create-page`, {
        item_ids: selectedForShare,
        title: shareTitle,
        message: shareMessage,
      }, getAuthHeader());

      const shareUrl = response.data.share_url;
      setLastShareUrl(shareUrl);
      const subject = encodeURIComponent(shareTitle || 'Vehicle Catalog');
      const body = encodeURIComponent(
        `${shareMessage ? shareMessage + '\n\n' : ''}${response.data.item_count} vehicle(s)\n\nView catalog: ${shareUrl}`
      );
      const mailUrl = `mailto:?subject=${subject}&body=${body}`;

      const a = document.createElement('a');
      a.href = mailUrl;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast.success('Opening email client...');
      setShowShareModal(false);
      setSelectedForShare([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
    }
    setSharing(false);
  };

  const handleCopyShareLink = async () => {
    if (selectedForShare.length === 0) {
      toast.error('Please select at least one catalog item to share');
      return;
    }
    setSharing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/catalog/share/create-page`, {
        item_ids: selectedForShare,
        title: shareTitle,
        message: shareMessage,
      }, getAuthHeader());

      const shareUrl = response.data.share_url;
      setLastShareUrl(shareUrl);
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
    }
    setSharing(false);
  };

  const resetForm = () => {
    setFormData({
      brand: '',
      model: '',
      variant: '',
      color: '',
      title: '',
      description: '',
      current_price: 0,
      old_price: null,
      custom_discount: 0,
      specifications: {},
      is_active: true
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getImageUrl = (image) => {
    if (!image?.storage_path) return null;
    const token = localStorage.getItem('token');
    return `${BACKEND_URL}/api/catalog/image/${image.storage_path}?auth=${token}`;
  };

  // Group items by brand
  const groupedByBrand = catalogItems.reduce((acc, item) => {
    const brand = item.brand || 'Other';
    if (!acc[brand]) acc[brand] = [];
    acc[brand].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-800 to-slate-900 text-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold">Catalog Master</h1>
              <p className="text-sm text-white/70">Manage vehicle catalogs for sharing</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowShareModal(true); if (selectedForShare.length === 0) selectAllForShare(); }}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-sm font-medium flex items-center gap-2"
              data-testid="share-whatsapp-btn"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.637-1.467A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.175 0-4.19-.6-5.925-1.64l-.425-.253-2.75.87.892-2.675-.278-.44A9.71 9.71 0 012.25 12C2.25 6.624 6.624 2.25 12 2.25S21.75 6.624 21.75 12 17.376 21.75 12 21.75z"/></svg>
              Share on WhatsApp {selectedForShare.length > 0 ? `(${selectedForShare.length})` : ''}
            </button>
            <button
              onClick={handleSyncPrices}
              disabled={syncing}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              data-testid="sync-prices-btn"
            >
              <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {syncing ? 'Syncing...' : 'Sync Prices'}
            </button>
            <button
              onClick={() => { resetForm(); setEditItem(null); setShowModal(true); }}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 rounded-lg text-sm font-medium flex items-center gap-2"
              data-testid="add-catalog-item-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Catalog Item
            </button>
          </div>
        </div>
      </header>

      {/* Filter Bar */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Filter by Brand:</label>
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="">All Brands</option>
              {brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
          <span className="text-sm text-gray-500">
            {catalogItems.length} items
          </span>
        </div>
      </div>

      {/* Catalog Items by Brand */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {Object.keys(groupedByBrand).length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-700">No Catalog Items</h3>
            <p className="text-gray-500 mt-1">Click "Add Catalog Item" to create your first catalog entry</p>
          </div>
        ) : (
          Object.entries(groupedByBrand).map(([brand, items]) => (
            <div key={brand} className="mb-8">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                {brand} Collection ({items.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(item => (
                  <div key={item.id} className={`relative bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow ${selectedForShare.includes(item.id) ? 'ring-2 ring-green-500' : ''}`}>
                    {/* Selection Checkbox */}
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        checked={selectedForShare.includes(item.id)}
                        onChange={() => toggleShareItem(item.id)}
                        className="w-5 h-5 text-green-600 border-2 border-white rounded shadow cursor-pointer"
                        data-testid={`select-item-${item.id}`}
                      />
                    </div>
                    {/* Image Section */}
                    <div className="relative h-48 bg-gray-100">
                      {item.images?.length > 0 ? (
                        <img
                          src={getImageUrl(item.images.find(img => img.is_primary) || item.images[0])}
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      {!item.is_active && (
                        <div className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs rounded">
                          Inactive
                        </div>
                      )}
                      {/* Image count badge */}
                      {item.images?.length > 1 && (
                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
                          {item.images.length} images
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="font-bold text-gray-800">{item.title}</h3>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.description || `${item.model} - ${item.variant}`}</p>
                      {item.color && (
                        <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          {item.color}
                        </span>
                      )}
                      <div className="mt-3">
                        <span className="font-bold text-teal-600">{formatCurrency(item.current_price)}</span>
                        {item.old_price && (
                          <span className="ml-2 text-sm text-gray-400 line-through">{formatCurrency(item.old_price)}</span>
                        )}
                      </div>
                      {item.custom_discount > 0 && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                          Discount: {formatCurrency(item.custom_discount)}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs hover:bg-purple-200 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {uploadingImage ? 'Uploading...' : 'Add Image'}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => handleImageUpload(e, item.id)}
                            disabled={uploadingImage}
                          />
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                          title="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                          title="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Images Management (if has images) */}
                    {item.images?.length > 0 && (
                      <div className="px-4 py-2 border-t bg-gray-50">
                        <p className="text-xs text-gray-500 mb-2">Images:</p>
                        <div className="flex gap-2 flex-wrap">
                          {item.images.map(img => (
                            <div key={img.id} className="relative group">
                              <img
                                src={getImageUrl(img)}
                                alt=""
                                className={`w-12 h-12 object-cover rounded ${img.is_primary ? 'ring-2 ring-teal-500' : ''}`}
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-1">
                                {!img.is_primary && (
                                  <button
                                    onClick={() => handleSetPrimaryImage(item.id, img.id)}
                                    className="p-1 text-white hover:text-yellow-400"
                                    title="Set as primary"
                                  >
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteImage(item.id, img.id)}
                                  className="p-1 text-white hover:text-red-400"
                                  title="Remove"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-lg font-bold">
                {editItem ? 'Edit Catalog Item' : 'Add Catalog Item'}
              </h2>
              <button onClick={() => { setShowModal(false); setEditItem(null); }} className="p-2 hover:bg-white/10 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Vehicle Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand *</label>
                  <select
                    value={formData.brand}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  >
                    <option value="">Select Brand</option>
                    {masterBrands.map(brand => (
                      <option key={brand.id || brand.name} value={brand.name}>{brand.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                  <select
                    value={formData.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    disabled={!formData.brand}
                  >
                    <option value="">Select Model</option>
                    {getModels().map(model => (
                      <option key={model.id || model.name} value={model.name}>{model.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variant *</label>
                  <select
                    value={formData.variant}
                    onChange={(e) => handleInputChange('variant', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                    disabled={!formData.model}
                  >
                    <option value="">Select Variant</option>
                    {getVariants().map(variant => (
                      <option key={variant.id || variant.name} value={variant.name}>{variant.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <select
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    disabled={!formData.variant}
                  >
                    <option value="">Select Color</option>
                    {getColors().map(color => (
                      <option key={color.id || color.name} value={color.name}>{color.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Title & Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., BG C12 Max 3.0"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="e.g., IDC Range 125kms, Smart Features"
                />
              </div>

              {/* Pricing */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Price</label>
                  <input
                    type="number"
                    value={formData.current_price}
                    onChange={(e) => setFormData(prev => ({ ...prev, current_price: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    step="0.01"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-synced from Vehicle Price Master</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Old Price (Strikethrough)</label>
                  <input
                    type="number"
                    value={formData.old_price || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, old_price: e.target.value ? parseFloat(e.target.value) : null }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    step="0.01"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Discount</label>
                  <input
                    type="number"
                    value={formData.custom_discount}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_discount: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg"
                    step="0.01"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Deducted from synced price</p>
                </div>
              </div>

              {/* Active Status */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Active (visible in catalog)</span>
              </label>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditItem(null); }}
                  className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  {editItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Catalog Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" data-testid="share-modal">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                Share Catalog
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={shareTitle}
                  onChange={e => setShareTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Vehicle Catalog"
                  data-testid="share-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                <textarea
                  value={shareMessage}
                  onChange={e => setShareMessage(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Check out our latest vehicles..."
                  data-testid="share-message-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number (for WhatsApp)</label>
                <input
                  type="tel"
                  value={sharePhone}
                  onChange={e => setSharePhone(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="919876543210 (with country code)"
                  data-testid="share-phone-input"
                />
              </div>
              <div className="bg-teal-50 rounded-lg p-3 text-sm text-teal-700">
                <strong>{selectedForShare.length}</strong> vehicle(s) selected. A beautiful catalog page will be created and shared.
              </div>
              {lastShareUrl && (
                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500 break-all border" data-testid="last-share-url">
                  Last link: <a href={lastShareUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">{lastShareUrl}</a>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Cancel
                </button>
                <div className="flex-1" />
                <button
                  onClick={handleCopyShareLink}
                  disabled={sharing || selectedForShare.length === 0}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="share-copy-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  Copy Link
                </button>
                <button
                  onClick={handleShareEmail}
                  disabled={sharing || selectedForShare.length === 0}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="share-email-btn"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  Email
                </button>
                <button
                  onClick={handleShareWhatsApp}
                  disabled={sharing || selectedForShare.length === 0}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="share-send-btn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.637-1.467A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.175 0-4.19-.6-5.925-1.64l-.425-.253-2.75.87.892-2.675-.278-.44A9.71 9.71 0 012.25 12C2.25 6.624 6.624 2.25 12 2.25S21.75 6.624 21.75 12 17.376 21.75 12 21.75z"/></svg>
                  {sharing ? 'Creating...' : 'WhatsApp'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
